import { Box, Chip, Stack, styled, Typography } from '@mui/material';
import { addYears, isAfter, isBefore, isSameDay, sub } from 'date-fns';
import { get, isEmpty, isFunction } from 'lodash';
import { BaseFilter } from 'mui-enhanced-table';
import { TableRow } from '..';
import { formatDate, generateNamesObject } from '../../../utils/stringUtils';
import { ClearableDatePicker } from '../../ClearableDatePicker';

export const dateRangeFilterClasses = generateNamesObject('container', 'datePicker', 'datePickerInput', 'pickers');

const Container = styled(Box)(({ theme }) => ({
    [`& .${dateRangeFilterClasses.datePicker}`]: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(2),
        width: 160,
    },
    [`& .${dateRangeFilterClasses.pickers}`]: {
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
        },
    },
}));

export type PickerValueType = Date | null;

export type PickerPresetType = {
    label: string;
    startDate: Date;
    endDate: Date;
};

type Props = {
    fromDate?: PickerValueType;
    toDate?: PickerValueType;
    dateFormat?: string;
    presets?: PickerPresetType[];
    onChange?: (fromDate: PickerValueType, toDate: PickerValueType) => void;
};

type State = {
    fromDate: PickerValueType;
    toDate: PickerValueType;
};

export class DateRangeFilter extends BaseFilter<Props, State> {
    state: State = {
        fromDate: this.props.fromDate ? new Date(this.props.fromDate) : null,
        toDate: this.props.toDate ? addYears(new Date(), 1) : null,
    };

    componentDidMount = () => {
        this.props.onChange?.(this.state.fromDate, this.state.toDate);
    };

    handleFromDateChange = (date: PickerValueType) => {
        if (date) {
            this.setState({ fromDate: date }, this.updateFilter);
        }
    };

    handleToDateChange = (date: PickerValueType) => {
        if (date) {
            this.setState({ toDate: date }, this.updateFilter);
        }
    };

    handleFromDateClear = () => {
        this.setState({ fromDate: null }, this.updateFilter);
    };

    handleToDateClear = () => {
        this.setState({ toDate: null }, this.updateFilter);
    };

    filterData = (item: TableRow) => {
        let match = true;
        const filterBy = this.props.filterBy;
        const date = formatDate(get(item.data, isFunction(filterBy) ? filterBy(item) : filterBy || ''), {
            withTimeZone: false,
        });
        const { fromDate, toDate } = this.state;

        if (fromDate) {
            match = isSameDay(date, fromDate) || isAfter(date, fromDate);
        }

        if (toDate) {
            match = match && (isSameDay(date, toDate) || isBefore(date, toDate));
        }

        return match;
    };

    updateFilter = () => {
        const { data, onChange, onFilterUpdate } = this.props;

        const { fromDate, toDate } = this.state;

        onChange?.(fromDate, toDate);
        onFilterUpdate(data.filter(this.filterData).map((item) => item.id));
    };

    render() {
        const { name, dateFormat = 'MMM dd, yyyy', presets = [] } = this.props;
        const { fromDate, toDate } = this.state;

        return (
            <Stack width="100%" direction="row" rowGap={1} alignItems="end" flexWrap="wrap">
                <Container>
                    <Typography variant="overline">{name}</Typography>
                    <Box className={dateRangeFilterClasses.pickers}>
                        <ClearableDatePicker
                            disableFuture
                            clearable
                            label="From"
                            format={dateFormat}
                            className={dateRangeFilterClasses.datePicker}
                            value={fromDate}
                            shouldDisableDate={(date) =>
                                (date && toDate && isAfter(date, toDate)) ||
                                (date && isAfter(date, Date.now())) ||
                                false
                            }
                            onChange={this.handleFromDateChange}
                            onClear={this.handleFromDateClear}
                            InputProps={{
                                size: 'small',
                            }}
                        />

                        <ClearableDatePicker
                            clearable
                            label="To"
                            format={dateFormat}
                            className={dateRangeFilterClasses.datePicker}
                            value={toDate}
                            shouldDisableDate={(date) =>
                                (date && fromDate && isBefore(date, sub(fromDate, { days: 1 }))) || false
                            }
                            onChange={this.handleToDateChange}
                            onClear={this.handleToDateClear}
                            InputProps={{
                                size: 'small',
                            }}
                        />
                    </Box>
                </Container>

                {!isEmpty(presets) && (
                    <Stack spacing={1} mb={1} direction="row">
                        {presets.map(({ label, startDate, endDate }) => {
                            const isSelected =
                                !!fromDate && !!toDate && isSameDay(startDate, fromDate) && isSameDay(endDate, toDate);
                            return (
                                <Chip
                                    key={label}
                                    label={label}
                                    size="small"
                                    sx={{ width: 'fit-content' }}
                                    variant={isSelected ? 'filled' : 'outlined'}
                                    onClick={() => {
                                        const newFromDate = isSelected ? null : startDate;
                                        const newToDate = isSelected ? null : endDate;
                                        this.setState({ fromDate: newFromDate, toDate: newToDate }, this.updateFilter);
                                    }}
                                />
                            );
                        })}
                    </Stack>
                )}
            </Stack>
        );
    }
}
